/**
 * Safely extracts a user-friendly error message from various error formats.
 * Handles FastAPI/Pydantic validation errors, plain strings, arrays, and objects.
 * 
 * @param {any} error - The error data from API response
 * @param {string} fallback - Fallback message if extraction fails
 * @returns {string} A user-friendly error message
 */
export const extractErrorMessage = (error, fallback = 'An unexpected error occurred.') => {
  // If it's already a string, return it
  if (typeof error === 'string') {
    return error;
  }

  // If it's null or undefined, return fallback
  if (error == null) {
    return fallback;
  }

  // If it's an array (Pydantic validation errors)
  if (Array.isArray(error)) {
    const messages = error
      .map((item) => {
        if (typeof item === 'string') return item;
        if (typeof item === 'object' && item !== null) {
          // Pydantic error format: { type, loc, msg, input, ctx }
          return item.msg || item.message || item.detail || JSON.stringify(item);
        }
        return String(item);
      })
      .filter(Boolean);
    
    return messages.length > 0 ? messages.join('. ') : fallback;
  }

  // If it's an object
  if (typeof error === 'object') {
    // Check common error message fields
    if (error.msg) return error.msg;
    if (error.message) return error.message;
    if (error.detail) {
      // detail could also be an array or object
      return extractErrorMessage(error.detail, fallback);
    }
    if (error.error) return error.error;
    
    // If it has loc and msg (Pydantic validation error format)
    if (error.loc && error.msg) {
      const field = Array.isArray(error.loc) ? error.loc.join('.') : error.loc;
      return `${field}: ${error.msg}`;
    }

    // Try to stringify, but return fallback if it's a complex object
    try {
      const str = JSON.stringify(error);
      // Don't return raw JSON to users
      return fallback;
    } catch {
      return fallback;
    }
  }

  // For any other type, convert to string
  return String(error) || fallback;
};

/**
 * Extracts error message from an Axios error response
 * 
 * @param {Error} err - The error object from axios catch
 * @param {string} fallback - Fallback message if extraction fails
 * @returns {string} A user-friendly error message
 */
export const getApiErrorMessage = (err, fallback = 'An unexpected error occurred.') => {
  // Handle axios error response
  if (err?.response?.data) {
    const data = err.response.data;
    
    // FastAPI typically uses 'detail' for error messages
    if (data.detail !== undefined) {
      return extractErrorMessage(data.detail, fallback);
    }
    
    // Some APIs use 'message' or 'error'
    if (data.message) return extractErrorMessage(data.message, fallback);
    if (data.error) return extractErrorMessage(data.error, fallback);
    
    // Try to extract from the whole data object
    return extractErrorMessage(data, fallback);
  }

  // Handle network errors
  if (err?.message) {
    if (err.message.includes('Network Error')) {
      return 'Unable to connect to the server. Please check your connection and try again.';
    }
    if (err.code === 'ECONNABORTED') {
      return 'Request timed out. Please try again.';
    }
    return err.message;
  }

  return fallback;
};

export default { extractErrorMessage, getApiErrorMessage };
